import React, { memo, useState, useEffect, useContext } from "react";
import { Space, Checkbox } from "antd";
import { BsBoxes } from "react-icons/bs";
import { BCC_MODULE_TYPES, KeywordTypeValues } from "@ts/common-types";
import { campaignCreationContext } from "@ts/constants";

interface IModalTitleFiltersProps {
    moduleType: BCC_MODULE_TYPES;
}

const ModalTitleFilters: React.FC<IModalTitleFiltersProps> = memo(
    ({ moduleType }) => {
        const { modalParams, setModalParams } = useContext(
            campaignCreationContext
        );
        const [selectedTypes, setSelectedTypes] = useState<string[]>(() => {
            return modalParams?.selectedKwTypes?.length > 0
                ? modalParams.selectedKwTypes
                : [KeywordTypeValues.BRANDED, KeywordTypeValues.GENERIC, KeywordTypeValues.COMPETITION];
        });

        useEffect(() => {
            if (moduleType === BCC_MODULE_TYPES.KEYWORDS) {
                if (modalParams?.selectedKwTypes?.length > 0) {
                    setSelectedTypes(modalParams.selectedKwTypes);
                } else {
                    // Default to all checkboxes checked
                    setSelectedTypes([KeywordTypeValues.BRANDED, KeywordTypeValues.GENERIC, KeywordTypeValues.COMPETITION]);
                }
            }
        }, [
            modalParams?.clusterId,
            modalParams?.collectionId,
            modalParams?.selectedKwTypes,
            moduleType,
        ]);

        useEffect(() => {
            if (setModalParams && moduleType === BCC_MODULE_TYPES.KEYWORDS) {
                setModalParams((prev: any) => ({
                    ...(prev || {}),
                    selectedKwTypes: selectedTypes,
                }));
            }
        }, [selectedTypes, setModalParams, moduleType]);

        const allKeywordTypes = [
            KeywordTypeValues.BRANDED,
            KeywordTypeValues.GENERIC,
            KeywordTypeValues.COMPETITION,
        ];

        const handleGroupChange = (checkedValues: string[]) => {
            setSelectedTypes(checkedValues);
        };

        const handleAllChange = (checked: boolean) => {
            setSelectedTypes(checked ? allKeywordTypes : []);
        };

        const isAllChecked = selectedTypes.length === allKeywordTypes.length;

        // Icon and title rendering (reusable across modals)
        const renderIconAndTitle = (title: string) => (
            <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                <div
                    className="d-flex align-items-center justify-content-center rounded"
                    style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#f5f5f5",
                    }}
                >
                    <BsBoxes
                        style={{ fontSize: "18px", color: "#252b37" }}
                    />
                </div>
                <span
                    style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#252b37",
                    }}
                >
                    {title}
                </span>
            </div>
        );

        // For KEYWORDS modal: Show icon, title, and checkboxes
        if (moduleType === BCC_MODULE_TYPES.KEYWORDS) {
            return (
                <div className="d-flex justify-content-between align-items-center w-100">
                    {renderIconAndTitle("Select Targets")}
                    <Space className="me-4" size="middle">
                        <Checkbox
                            checked={isAllChecked}
                            onChange={(e) => handleAllChange(e.target.checked)}
                        >
                            All
                        </Checkbox>
                        <Checkbox.Group
                            value={selectedTypes}
                            onChange={handleGroupChange}
                            options={[
                                { label: "Branded", value: KeywordTypeValues.BRANDED },
                                { label: "Generic", value: KeywordTypeValues.GENERIC },
                                { label: "Competition", value: KeywordTypeValues.COMPETITION },
                            ]}
                        />
                    </Space>
                </div>
            );
        }

        // For other modal types: Just show icon and title
        return renderIconAndTitle("Create Cluster");
    }
);

export default ModalTitleFilters;

